import { Icon, Input, Spin } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { RequestMeetingModalReason } from '../../../modules/meeting/cancel/RequestMeetingDeclineModal'
import { InviteModalState } from '../../../modules/meeting/invite/MeetingInviteModal'
import { EmployeeDisplayTinyInfo, MeetingRequestDto, MeetingStepViewId, MeetingType, SkillEmployeeStatusEnum } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'
import PerDatePicker from '../DatePicker'
import PerFlexRow from '../FlexRow'
import RichTextBox from '../RichTextBox'
import PerText from '../Text'
import PerTimePicker from '../TimePicker'
import { AppraisalInviteSteps } from './AppraisalInviteSteps'
import { AppraisalUpdateInviteSteps } from './AppraisalUpdateInviteSteps'
import { EmployeeSelect } from './EmployeeSelect'
import { InviteModalContentUtils } from './inviteModalContentUtils'
import { InviteModalMeetingOverlap } from './InviteModalMeetingOverlap'
import { InviteModalRow } from './InviteModalRow'
import { MeetingInviteDurationSelect } from './MeetingInviteDurationSelect'
import { MeetingInviteOptionsSelect } from './MeetingInviteOptionsSelect'
import { ReducedStepsReasonSelect } from './ReducedStepsReasonSelect'
import { NonParticipantsSelect } from './NonParticipantsSelect'
import { AppraisalSeasonRow } from './AppraisalSeasonRow'
import { SkillStatusIcon, SkillStatusDescription } from '../../../modules/skillStatus/SkillStatusLabelComponent'
import PerIcon from '../Icon'
import { StatusMessageBox } from '../StatusMessageBox'
import { ValidationErrorsData } from '../../../shared/graphqlErrorHelper'

const antIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />

const getServerErrorMessage = (serverErrors: ValidationErrorsData[], genericMessage: string): string => {
  const errorKey = serverErrors[0]?.errorKey
  return errorKey?.startsWith('error.code') ? serverErrors[0]?.message || genericMessage : genericMessage
}

const getSubmitErrorMessage = (serverErrors: ValidationErrorsData[], genericMessage: string, reviewerNotAuthorized: string): string => {
  if (serverErrors.some((error) => error.errorKey === 'permission.not.granted.30095')) {
    return reviewerNotAuthorized
  }
  return serverErrors[0]?.message || genericMessage
}
export type EmployeeShort = {
  id: number
  legalFirstName: string
  legalLastName: string
}

export type InviteModalContentProps = {
  employeeList: EmployeeDisplayTinyInfo[]
  state: InviteModalState
  timelocal: TimeZoneAndLocale
  inviteModalContentUtils: InviteModalContentUtils
  onUpdate: (fieldname: string, value: unknown) => void
  onUpdateSteps: (stepId: MeetingStepViewId, value: boolean) => void
  requestedMeeting?: MeetingRequestDto
  isRefreshSkillStatusProcessing: boolean
  toggleRefreshSkillStatusProcessing: React.Dispatch<React.SetStateAction<boolean>>
  serverErrors?: ValidationErrorsData[]
}

const InviteModalContent: FC<InviteModalContentProps> = ({
  employeeList,
  state,
  timelocal,
  onUpdate,
  onUpdateSteps,
  requestedMeeting,
  inviteModalContentUtils,
  isRefreshSkillStatusProcessing,
  toggleRefreshSkillStatusProcessing,
  serverErrors,
}) => {
  const [t] = useTranslation()

  const {
    employee,
    meetingType,
    meetingDate,
    meetingAgenda,
    location,
    emailText,
    viewIds,
    reasonForReducedSteps,
    inviteOptions,
    tagAssignmentIsInProgress,
    isAppraisalEnabled,
    isLoadingInviteOptions,
    isLoadingASOptions,
    isLoadingNonParticipantsOptions,
    duration,
    additionalParticipants,
    additionalParticipantsOptions,
    appraisalSeason,
    skillStatusData,
    isLoadingSkillStatusData,
    loadError,
    isMeetingDateLoadError,
  } = state

  const containsStepViewId = (stepId: MeetingStepViewId): boolean => {
    if (viewIds) {
      return !!viewIds.find((x) => x === stepId)
    }
    return false
  }

  const appraisalWithReducedSteps =
    meetingType === MeetingType.Appraisal &&
    !(containsStepViewId(MeetingStepViewId.SelfReflection) && containsStepViewId(MeetingStepViewId.Evaluation))

  const isAppraisalMeeting = meetingType === MeetingType.Appraisal
  const isAppraisalUpdateMeeting = meetingType === MeetingType.AppraisalUpdate
  const isOtherMeeting = !(isAppraisalMeeting || isAppraisalUpdateMeeting)

  const hasEmail = isOtherMeeting || isAppraisalUpdateMeeting || isAppraisalMeeting
  const hasLoadError = !!(loadError && loadError.length)
  const isLoadingOptions = isLoadingInviteOptions || isLoadingNonParticipantsOptions || isLoadingASOptions || isLoadingSkillStatusData
  const agendaMargin = () => {
    if (!hasEmail) {
      return 'no'
    } else if (appraisalWithReducedSteps) {
      return undefined
    } else {
      return 'bigger'
    }
  }

  const meetingDateRow = (
    <InviteModalRow label={t('meetingInvite.meetingDate')}>
      <div data-testid="invite-modal-meeting-date">
        <PerDatePicker
          inputDate={meetingDate}
          onDateChanged={(utcDateChanged) => onUpdate('meetingDate', utcDateChanged)}
          timeZoneAndLocale={timelocal}
          dateFormat={'DMY'}
          disabled={false}
          disabledDate={inviteModalContentUtils.isMeetingDateNotAvailable}
        />
      </div>
    </InviteModalRow>
  )

  return (
    <>
      {requestedMeeting && <RequestMeetingModalReason reason={requestedMeeting.reasonForRequest} />}

      <InviteModalRow label={t('meetingInvite.meetingInviteEmployee')}>
        <EmployeeSelect employeeList={employeeList} employee={employee} onChangeEmployee={(employee) => onUpdate('employee', employee)} />
      </InviteModalRow>

      {hasLoadError ? (
        <>
          {isMeetingDateLoadError && meetingDateRow}
          <StatusMessageBox
            variant="error"
            withTopMargin={!!isMeetingDateLoadError}
            message={getServerErrorMessage(loadError, t('global.notifications.error.description'))}
          />
        </>
      ) : (
        <>
          {!isLoadingOptions || (requestedMeeting && !isLoadingSkillStatusData) ? (
            /*(
        skillStatusData ? (
          <InviteModalRow label={t('meetingInvite.emailText')} align="top" margin="no">
            <Card>
              <PerText appearance="info" fontWeight="bold">
                {t('meetingInvite.skillStatusInfoCard.section1')}
              </PerText>
              <PerSpacer mt="s"></PerSpacer>
              <PerText appearance="info" fontWeight="bold">
                {t('meetingInvite.skillStatusInfoCard.section2')}
              </PerText>
              <PerSpacer mt="s"></PerSpacer>
              <PerText appearance="info" fontWeight="bold">
                {t('meetingInvite.skillStatusInfoCard.section3')}
              </PerText>
              <PerSpacer mt="s"></PerSpacer>
              <PerText appearance="info" fontWeight="bold">
                {t('meetingInvite.skillStatusInfoCard.section4')}
              </PerText>
            </Card>
          </InviteModalRow>
        ) : */ <div data-testid="invite-options-wrapper">
              <InviteModalRow label={t('meetingInvite.skillStatus')}>
                <PerFlexRow direction="column">
                  <PerFlexRow align="middle">
                    {!!isRefreshSkillStatusProcessing ? (
                      <PerIcon size={24} name="loading_fill" color="secondary" spin />
                    ) : (
                      <SkillStatusIcon skillStatus={skillStatusData} size={24} tooltipVisible={false} />
                    )}
                    {!!skillStatusData && (
                      <PerText fontSize="s" ml="xs">
                        {t(
                          `profile.skillCenter.public.label.${
                            skillStatusData ? skillStatusData.skillStatus : SkillEmployeeStatusEnum.Unknown
                          }`,
                        )}
                      </PerText>
                    )}
                  </PerFlexRow>
                  <SkillStatusDescription
                    skillStatus={skillStatusData}
                    skillStatusErrorVisible={false}
                    iconVisible={false}
                    onClick={async () => {
                      toggleRefreshSkillStatusProcessing(true)
                    }}
                    isProcessing={isRefreshSkillStatusProcessing}
                  />
                </PerFlexRow>
              </InviteModalRow>
              {meetingDateRow}

              <InviteModalRow label={t('meetingInvite.meetingTime')}>
                <PerTimePicker
                  inputDate={meetingDate}
                  timeZoneAndLocale={timelocal}
                  onTimeChanged={(utcDateChanged) => onUpdate('meetingDate', utcDateChanged)}
                  timeFormat="HH:mm"
                  minuteStep={15}
                  hideDisabledOptions={true}
                />
                <InviteModalMeetingOverlap meetingId={state.meetingId} duration={state.duration} meetingDate={state.meetingDate} />
              </InviteModalRow>
              <InviteModalRow label={t('meetingInvite.duration')}>
                <MeetingInviteDurationSelect
                  duration={duration}
                  onChangeDuration={(durationChanged) => onUpdate('duration', durationChanged)}
                />
              </InviteModalRow>

              <InviteModalRow label={t('meetingInvite.appraisalSeason')}>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                <AppraisalSeasonRow appraisalSeason={appraisalSeason!} showIcon />
              </InviteModalRow>

              <InviteModalRow label={t('meetingInvite.meetingType')}>
                <MeetingInviteOptionsSelect
                  onChangeMeetingType={(type) => onUpdate('meetingType', type)}
                  inviteOptions={inviteOptions ? inviteOptions : []}
                  meetingType={requestedMeeting ? requestedMeeting.type : state.meetingType}
                  isPredefined={requestedMeeting ? true : false}
                  shouldDisableAppraisals={tagAssignmentIsInProgress || !isAppraisalEnabled}
                />
              </InviteModalRow>

              <InviteModalRow label={t('meetingInvite.location')}>
                <Input
                  type="text"
                  name="location"
                  value={location}
                  onChange={({ target: { value } }) => onUpdate('location', value)}
                  placeholder={t('meetingInvite.locationPlaceholder')}
                  data-e2e-modal-meeting-location
                />
              </InviteModalRow>

              <InviteModalRow label={t('meetingInvite.additionalParticipants')} margin="bigger">
                <NonParticipantsSelect
                  placeholderText={t('meetingInvite.additionalParticipantsPlaceholder')}
                  notFoundContentText={t('meetingInvite.notFoundEmployee')}
                  participantsOptions={
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    additionalParticipantsOptions!
                  }
                  onChangeParticipant={(participant) => onUpdate('additionalParticipants', [participant])}
                  additionalParticipantId={additionalParticipants.length ? additionalParticipants[0].id : undefined}
                />
              </InviteModalRow>

              <InviteModalRow label={t('meetingInvite.meetingAgenda')} align="top" margin={agendaMargin()}>
                {isAppraisalMeeting && viewIds && (
                  <div data-testid="invite-modal-step-list">
                    <AppraisalInviteSteps viewIds={viewIds} onCheckStep={(viewId, checked) => onUpdateSteps(viewId, checked)} />
                  </div>
                )}

                {isAppraisalUpdateMeeting && viewIds && (
                  <div data-testid="invite-modal-step-list">
                    <AppraisalUpdateInviteSteps viewIds={viewIds} onCheckStep={(viewId, checked) => onUpdateSteps(viewId, checked)} />
                  </div>
                )}

                {isOtherMeeting && (
                  <div data-e2e-modal-meeting-agenda-textarea data-testid="invite-modal-agenda">
                    <RichTextBox text={meetingAgenda ? meetingAgenda : ''} onChange={(text) => onUpdate('meetingAgenda', text)} />
                  </div>
                )}
              </InviteModalRow>

              {appraisalWithReducedSteps && (
                <div data-testid="invite-modal-reason-for-reduced-steps">
                  <InviteModalRow label={t('meetingInvite.reasonForReducedSteps.header')} margin="bigger">
                    <ReducedStepsReasonSelect
                      reasonForReducedSteps={reasonForReducedSteps}
                      onSetReason={(reason: string) => onUpdate('reasonForReducedSteps', reason)}
                    />
                  </InviteModalRow>
                </div>
              )}

              {hasEmail && (
                <InviteModalRow label={t('meetingInvite.emailText')} align="top" margin="no">
                  <div data-e2e-modal-meeting-email-textarea data-testid="invite-modal-email">
                    <RichTextBox text={emailText ? emailText : ''} onChange={(text) => onUpdate('emailText', text)} />
                  </div>
                </InviteModalRow>
              )}
            </div>
          ) : (
            <div style={{ height: '500px' }}>
              <PerFlexRow mt="l">
                <Spin indicator={antIcon} />
                <PerText ml={'s'} mt="xxs">
                  {t('meetingInvite.loading')}
                </PerText>
              </PerFlexRow>
            </div>
          )}
        </>
      )}
      {!hasLoadError && serverErrors && (
        <StatusMessageBox
          variant="error"
          withTopMargin
          message={getSubmitErrorMessage(
            serverErrors,
            t('global.notifications.error.description'),
            t('meetingInvite.reviewerNotAuthorized'),
          )}
        />
      )}
    </>
  )
}

export default InviteModalContent
